import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { ALLERGENS } from "@/data/allergens";
import { ALLERGY } from "@/constants/testIds";
import { useSheetHistory } from "@/hooks/useSheetHistory";

// `value` is the same comma-joined string GuestEntry always stored — this
// component only changes how it is edited, never what it holds. A returning
// guest's old free text keeps working: any token that does not match the
// catalog stays selected as a "legacy" chip (removable, never re-typeable),
// because it is still safety information even if it did not come from a list.

const CATALOG_BY_LOWER_VALUE = new Map(ALLERGENS.map((entry) => [entry.value.toLowerCase(), entry]));

const slugify = (token) => token.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function parseAllergyValue(value) {
  const tokens = String(value || "")
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

  const seen = new Set();
  const selectedKeys = new Set();
  const legacyTokens = [];

  for (const token of tokens) {
    const lower = token.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);

    const match = CATALOG_BY_LOWER_VALUE.get(lower);
    if (match) {
      selectedKeys.add(match.key);
    } else {
      legacyTokens.push(token);
    }
  }

  return { selectedKeys, legacyTokens };
}

// Catalog order for known values, then legacy tokens in their original order.
function serializeAllergyValue(selectedKeys, legacyTokens) {
  const known = ALLERGENS.filter((entry) => selectedKeys.has(entry.key)).map((entry) => entry.value);
  return [...known, ...legacyTokens].join(", ");
}

const AllergyPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useSheetHistory(open, () => {
    setOpen(false);
    setSearch("");
  });

  const { selectedKeys, legacyTokens } = useMemo(() => parseAllergyValue(value), [value]);

  const toggle = (key) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(serializeAllergyValue(next, legacyTokens));
  };

  const removeLegacy = (token) => {
    onChange(serializeAllergyValue(selectedKeys, legacyTokens.filter((existing) => existing !== token)));
  };

  const chips = [
    ...ALLERGENS.filter((entry) => selectedKeys.has(entry.key)).map((entry) => ({
      testid: ALLERGY.chip(entry.key),
      label: t(`allergens.${entry.key}`),
      onRemove: () => toggle(entry.key),
    })),
    ...legacyTokens.map((token) => ({
      testid: ALLERGY.chip(slugify(token)),
      label: token,
      onRemove: () => removeLegacy(token),
    })),
  ];

  const query = search.trim().toLowerCase();
  const filtered = query
    ? ALLERGENS.filter((entry) => {
        const label = t(`allergens.${entry.key}`).toLowerCase();
        return label.includes(query) || entry.value.toLowerCase().includes(query);
      })
    : ALLERGENS;

  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block px-1">
        {t("guestEntry.allergyLabel")}
      </label>
      <div
        role="button"
        tabIndex={0}
        data-testid={ALLERGY.trigger}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter") {
            setOpen(true);
          } else if (e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="flex w-full min-h-14 items-center bg-white border border-neutral-200 rounded-2xl px-4 py-2 text-left"
      >
        {chips.length > 0 ? (
          <div className="flex w-full flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip.testid}
                data-testid={chip.testid}
                className="rounded-full bg-neutral-100 h-8 px-3 inline-flex items-center gap-1 text-xs font-semibold text-neutral-800"
              >
                {chip.label}
                <button
                  type="button"
                  aria-label={t("common.remove")}
                  onClick={(e) => {
                    e.stopPropagation();
                    chip.onRemove();
                  }}
                  className="shrink-0"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-base font-medium text-neutral-400">{t("guestEntry.allergyPlaceholder")}</span>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center"
          onClick={() => {
            setOpen(false);
            setSearch("");
          }}
        >
          <div
            data-testid={ALLERGY.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={t("guestEntry.allergyLabel")}
            className="w-full sm:max-w-md max-h-[80vh] bg-white rounded-t-3xl sm:rounded-3xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-4 pt-4 pb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 px-1 py-1">
                {t("guestEntry.allergyLabel")}
              </div>
              <input
                data-testid={ALLERGY.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("allergyPicker.searchPlaceholder")}
                className="mt-1 w-full h-11 bg-neutral-100 rounded-xl px-4 text-sm font-medium text-neutral-900 placeholder:text-neutral-400 outline-none"
              />
            </div>

            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {filtered.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-neutral-400">{t("allergyPicker.empty")}</div>
              ) : (
                filtered.map((entry) => {
                  const checked = selectedKeys.has(entry.key);
                  return (
                    <button
                      key={entry.key}
                      type="button"
                      data-testid={ALLERGY.option(entry.key)}
                      aria-pressed={checked}
                      onClick={() => toggle(entry.key)}
                      className="w-full min-h-12 flex items-center justify-between gap-3 px-2 rounded-xl text-left text-base font-semibold text-neutral-900 active:bg-neutral-50 transition-colors"
                    >
                      {t(`allergens.${entry.key}`)}
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${
                          checked ? "bg-neutral-900 text-white" : "border border-neutral-300"
                        }`}
                      >
                        {checked && <Check size={14} />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="shrink-0 p-4 pt-2">
              <button
                type="button"
                data-testid={ALLERGY.done}
                onClick={() => {
                  setOpen(false);
                  setSearch("");
                }}
                className="w-full h-12 rounded-2xl bg-neutral-900 text-white font-bold active:scale-[0.98] transition-transform"
              >
                {t("allergyPicker.done")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllergyPicker;
